import React, { useCallback, useMemo, useEffect } from "react";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  MarkerType,
  Background,
  Controls,
  MiniMap,
  Node,
  useReactFlow,
  Panel,
  BackgroundVariant,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { TaskDefinition, TaskType } from "../types";
import FlowTaskNode from "./FlowTaskNode";

interface PlanFlowEditorProps {
  tasks: TaskDefinition[];
  onTasksChange: (tasks: TaskDefinition[]) => void;
  readOnly?: boolean;
}

const nodeWidth = 260;
const nodeHeight = 120;

const nodeTypes = {
  task: FlowTaskNode,
};

const getLayoutedElements = (
  tasks: TaskDefinition[],
  onTasksChange?: (tasks: TaskDefinition[]) => void,
) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setGraph({ rankdir: "LR" });
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Create Nodes
  const nodes: Node[] = tasks
    .filter((t) => t.type !== TaskType.SECTION)
    .map((t) => ({
      id: t.id,
      type: "task",
      data: {
        label: t.name,
        onLabelChange: (newLabel: string) => {
          if (onTasksChange) {
            const newTasks = tasks.map((tt) =>
              tt.id === t.id ? { ...tt, name: newLabel } : tt,
            );
            onTasksChange(newTasks);
          }
        },
      },
      position: { x: 0, y: 0 },
    }));

  // Create Edges
  const edges: Edge[] = [];
  tasks.forEach((t) => {
    if (t.type === TaskType.SECTION) return;
    t.dependencyIds.forEach((depId) => {
      // Only add edge if dependency exists and is not a section (sanity check)
      const depTask = tasks.find((dt) => dt.id === depId);
      if (depTask && depTask.type !== TaskType.SECTION) {
        edges.push({
          id: `${depId}-${t.id}`,
          source: depId,
          target: t.id,
          type: "default",
          animated: true,
          style: { stroke: "#94a3b8", strokeWidth: 2 }, // Slate-400
        });
      }
    });
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };
  });

  return { nodes, edges };
};

const PlanFlowEditor: React.FC<PlanFlowEditorProps> = ({
  tasks,
  onTasksChange,
  readOnly = false,
}) => {
  // We need to wrap getLayoutedElements to access onTasksChange closure
  const layoutWrapper = useCallback(() => {
    return getLayoutedElements(tasks, onTasksChange);
  }, [tasks, onTasksChange]);

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => layoutWrapper(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tasks.length,
      JSON.stringify(tasks.map((t) => t.dependencyIds)),
      JSON.stringify(tasks.map((t) => t.name)),
    ],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes/edges when tasks prop changes
  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = layoutWrapper();
    setNodes(newNodes);
    setEdges(newEdges);
  }, [layoutWrapper, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => {
      if (readOnly) return;
      const { source, target } = params;
      if (!source || !target || source === target) return;

      setEdges((eds) =>
        addEdge({ ...params, type: "smoothstep", animated: true }, eds),
      );

      const newTasks = tasks.map((t) => {
        if (t.id === target) {
          if (t.dependencyIds.includes(source)) return t;
          return { ...t, dependencyIds: [...t.dependencyIds, source] };
        }
        return t;
      });
      onTasksChange(newTasks);
    },
    [readOnly, tasks, onTasksChange, setEdges],
  );

  const onEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      if (readOnly) return;
      let newTasks = [...tasks];
      deletedEdges.forEach((edge) => {
        const { source, target } = edge;
        newTasks = newTasks.map((t) => {
          if (t.id === target) {
            return {
              ...t,
              dependencyIds: t.dependencyIds.filter((id) => id !== source),
            };
          }
          return t;
        });
      });
      onTasksChange(newTasks);
    },
    [readOnly, tasks, onTasksChange],
  );

  return (
    <div className="h-full w-full bg-slate-50 dark:bg-slate-900 border-none outline-none">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgesDelete={onEdgesDelete}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-right"
        nodesConnectable={!readOnly}
        nodesDraggable={true}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          type: "default",
          markerEnd: { type: MarkerType.ArrowClosed, color: "#94a3b8" },
          style: { strokeWidth: 2, stroke: "#94a3b8" },
        }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={1}
          className="dark:bg-slate-950 bg-slate-50"
          color="#cbd5e1"
        />
        <Controls className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-sm [&>button]:bg-white [&>button]:dark:bg-slate-800 [&>button]:border-slate-200 [&>button]:dark:border-slate-700 [&_svg]:fill-slate-500 [&_svg]:dark:fill-slate-400" />
        <MiniMap
          nodeColor={() => "#6366f1"}
          maskColor="rgba(0, 0, 0, 0.1)"
          className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-sm"
        />
        {readOnly && (
          <Panel
            position="top-right"
            className="bg-white/80 dark:bg-slate-800/80 p-2 rounded text-xs text-slate-500 dark:text-slate-400 border dark:border-slate-700"
          >
            Read Only
          </Panel>
        )}
      </ReactFlow>
    </div>
  );
};

export default PlanFlowEditor;
